/**
 * Form fields for the Restore Database dialog.
 */

import { AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import type { MssqlRecoveryState } from '../types/backupRestore';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
const RECOVERY_STATES: { value: MssqlRecoveryState; label: string; description: string }[] = [
  { value: 'RECOVERY', label: 'RECOVERY', description: 'Recover database and make it operational' },
  { value: 'NORECOVERY', label: 'NORECOVERY', description: 'Leave database restoring for additional restores' },
  { value: 'STANDBY', label: 'STANDBY', description: 'Leave database in read-only mode' },
];

type RestoreFormFieldsProps = {
  serverFilePath: string;
  onServerFilePathChange: (value: string) => void;
  targetDatabaseName: string;
  onTargetDatabaseNameChange: (value: string) => void;
  recoveryState: MssqlRecoveryState;
  onRecoveryStateChange: (value: MssqlRecoveryState) => void;
  overwriteExisting: boolean;
  onOverwriteExistingChange: (value: boolean) => void;
  databaseExists: boolean;
  isCheckingDb: boolean;
  onUseServerDefault?: () => void;
  isDetectingBackupDirectory?: boolean;
  serverDefaultBackupDirectory?: string | null;
  onBrowseServerFile?: () => void;
  databaseNameHint: string;
};

export function RestoreFormFields({
  serverFilePath,
  onServerFilePathChange,
  targetDatabaseName,
  onTargetDatabaseNameChange,
  recoveryState,
  onRecoveryStateChange,
  overwriteExisting,
  onOverwriteExistingChange,
  databaseExists,
  isCheckingDb,
  onUseServerDefault,
  isDetectingBackupDirectory,
  serverDefaultBackupDirectory,
  onBrowseServerFile,
  databaseNameHint,
}: RestoreFormFieldsProps) {
  return (
    <>
      {/* Server file path */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="server-file-path">Backup file path on SQL Server</Label>
          <div className="flex items-center gap-2">
            {onUseServerDefault && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onUseServerDefault}
                disabled={isDetectingBackupDirectory}
                data-testid="restore-use-server-default"
              >
                {isDetectingBackupDirectory ? (
                  <>
                    <RandomDotmSpinner size={12} />
                    Detecting...
                  </>
                ) : (
                  'Use server default'
                )}
              </Button>
            )}
            {onBrowseServerFile && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onBrowseServerFile}
                data-testid="restore-browse-server-file"
              >
                Browse...
              </Button>
            )}
          </div>
        </div>
        <Input
          id="server-file-path"
          value={serverFilePath}
          onChange={(e) => onServerFilePathChange(e.target.value)}
          placeholder="C:\\Backups\\database.bak"
          data-testid="restore-server-path-input"
        />
        <p className="text-xs text-muted-foreground">
          Example: C:\SQLBackups\{databaseNameHint}.bak or /var/opt/mssql/backups/{databaseNameHint}.bak
        </p>
        {serverDefaultBackupDirectory && (
          <p className="text-xs text-muted-foreground">
            Server default backup folder:{' '}
            <span className="font-mono">{serverDefaultBackupDirectory}</span>
          </p>
        )}
      </div>

      {/* Target database name */}
      <div className="space-y-2">
        <Label htmlFor="target-database">Target database name</Label>
        <Input
          id="target-database"
          value={targetDatabaseName}
          onChange={(e) => onTargetDatabaseNameChange(e.target.value)}
          placeholder="DatabaseName"
          data-testid="restore-target-database-input"
        />
        {databaseExists && (
          <p className="text-xs text-warning flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Database &quot;{targetDatabaseName}&quot; already exists
          </p>
        )}
        {isCheckingDb && (
          <p className="text-xs text-muted-foreground">Checking...</p>
        )}
      </div>

      {/* Recovery state */}
      <div className="space-y-3">
        <Label htmlFor="recovery-state">Recovery state</Label>
        <Select value={recoveryState} onValueChange={(v) => onRecoveryStateChange(v as MssqlRecoveryState)}>
          <SelectTrigger id="recovery-state" data-testid="restore-recovery-state-select" className="h-auto py-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="p-2">
            {RECOVERY_STATES.map((state) => (
              <SelectItem key={state.value} value={state.value} className="py-2">
                <div className="flex flex-col items-start text-left">
                  <span>{state.label}</span>
                  <span className="text-xs text-muted-foreground">{state.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Overwrite existing option */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="overwrite-existing"
          checked={overwriteExisting}
          onCheckedChange={(checked) => onOverwriteExistingChange(checked === true)}
          data-testid="restore-overwrite-checkbox"
        />
        <Label htmlFor="overwrite-existing" className="text-sm font-normal cursor-pointer">
          Overwrite existing database (WITH REPLACE)
        </Label>
      </div>
    </>
  );
}
